import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import * as PurgatoryActions from '../../actions/purgatory'
import { StringUtils } from '../../utils'
import {
    Button,
    Container,
    Description,
    ErrorMessage,
    GlobalStyle,
    Input,
    InputGroup,
    Label,
    MessageBoard,
    SuccessMessage,
} from './SetupDevice.style'
import { usePageTitle } from '../../actions/app'

interface UrlParams {
    device?: string
}

function SetupDevice(): JSX.Element {
    usePageTitle()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { device } = useParams<UrlParams>()
    const [fixtureCode, setFixtureCode] = React.useState<string>('')
    const [storeCode, setStoreCode] = React.useState<string>('')
    const [sku, setSku] = React.useState<string>('')
    const [error, setError] = React.useState<string | null>(null)
    const [progress, setProgress] = React.useState<number>(0)
    const [, setStep] = React.useState<string>('')

    const mac: string = StringUtils.formatMacFromID(device || '')

    const onChangeFixtureCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setError(null)
        setFixtureCode(e.target.value)
    }

    const onChangeStoreCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setError(null)
        setStoreCode(e.target.value)
    }

    const onChangeSku = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setError(null)
        setSku(e.target.value)
    }

    const onSubmit = async (): Promise<void> => {
        setError(null)
        setProgress(0)
        setStep('')

        if (!mac) {
            setError('No MAC selected. You need to scan a device first')
            return
        } else if (!fixtureCode || !storeCode || !sku) {
            setError('You must correctly populate all three fields in order to configure this device')
            return
        } else if (storeCode.length < 4) {
            setError('Store Code is too short')
            return
        } else if (storeCode.length > 5) {
            setError('Store Code is too long')
            return
        } else if (!sku.match(/\d+/)) {
            setError('SKU should only include numbers')
            return
        } else if (sku.length < 4) {
            setError('SKU is too short')
            return
        } else if (sku.length > 6) {
            setError('SKU is too long')
            return
        }

        let fixedStoreCode = storeCode

        while (fixedStoreCode.length < 5) {
            fixedStoreCode = '0' + fixedStoreCode
        }

        setProgress(0.00001)

        const action = await dispatch(
            PurgatoryActions.configureDeviceUnattendedly(
                {
                    fixtureCode,
                    storeCode: fixedStoreCode,
                    sku,
                    mac,
                }
            )
        )

        if (action.type !== PurgatoryActions.ActionTypes.CONFIGURE_DEVICE_UNNATENDENDLY_FAILURE) {
            setProgress(1)
            return
        }

        setProgress(0)
        setError(t('purgatory-backend'))
    }

    return (
        <>
            <GlobalStyle />
            <Container>
                <Description>
                    <h1>Configuring Device {mac}</h1>
                </Description>
                <InputGroup>
                    <Label>Store Number</Label>
                    <Input type="text" onChange={onChangeStoreCode} value={storeCode} />
                </InputGroup>
                <InputGroup>
                    <Label>Fixture Code</Label>
                    <Input type="text" onChange={onChangeFixtureCode} value={fixtureCode} />
                </InputGroup>
                <InputGroup>
                    <Label>Product SKU</Label>
                    <Input type="text" onChange={onChangeSku} value={sku} />
                </InputGroup>
                <MessageBoard>
                    {!error && progress === 1 && (
                        <SuccessMessage>{t('purgatory-backend', { context: 'success' })}</SuccessMessage>
                    )}
                    {error && <ErrorMessage>{t(error, { context: 'error' })}</ErrorMessage>}
                </MessageBoard>
                <InputGroup>
                    {progress < 1 && (
                        <Button onClick={onSubmit} disabled={progress > 0 || !!error}>Setup Device</Button>
                    )}
                </InputGroup>
                <Description>
                    <Link to={`/configure-legacy/${device}`}>Sign in</Link> to Hubble for more options
                </Description>
            </Container>
        </>
    )
}

export default SetupDevice
